/**
 * A small CSV reader for contact imports.
 *
 * Written rather than pulled in as a dependency because the requirement is
 * narrow, but it does handle the things real exports actually contain:
 * quoted fields, commas and newlines inside quotes, escaped quotes (""),
 * CRLF line endings, and the UTF-8 BOM Excel writes.
 */

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

/** Split CSV text into rows of raw cells. */
export function parseCsvCells(text: string): string[][] {
  // Excel prefixes a BOM, which would otherwise become part of the first header.
  const input = text.replace(/^﻿/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      // Treat CRLF as one break, and ignore blank lines.
      if (char === '\r' && input[i + 1] === '\n') i++;
      row.push(field);
      field = '';
      if (row.some((cell) => cell.trim() !== '')) rows.push(row);
      row = [];
    } else {
      field += char;
    }
  }

  row.push(field);
  if (row.some((cell) => cell.trim() !== '')) rows.push(row);
  return rows;
}

/** Parse CSV text into objects keyed by header. */
export function parseCsv(text: string): ParsedCsv {
  const cells = parseCsvCells(text);
  if (cells.length === 0) return { headers: [], rows: [] };

  const headers = cells[0].map((h) => h.trim());
  const rows = cells.slice(1).map((cells) => {
    const row: Record<string, string> = {};
    headers.forEach((header, i) => {
      row[header] = (cells[i] ?? '').trim();
    });
    return row;
  });
  return { headers, rows };
}

/** The contact fields an import can populate. */
export type ContactField =
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'title'
  | 'company'
  | 'companyWebsite'
  | 'linkedinUrl'
  | 'location'
  | 'phone';

/**
 * Header spellings seen in exports from the usual suspects (Apollo, Clay,
 * Sales Navigator, HubSpot, plain spreadsheets). Matched case-insensitively
 * with punctuation stripped, so "First Name", "first_name" and "FirstName"
 * all land on the same field.
 */
const HEADER_ALIASES: Record<ContactField, string[]> = {
  email: ['email', 'emailaddress', 'workemail', 'businessemail', 'primaryemail', 'mail'],
  firstName: ['firstname', 'first', 'givenname', 'forename'],
  lastName: ['lastname', 'last', 'surname', 'familyname'],
  title: ['title', 'jobtitle', 'position', 'role', 'headline'],
  company: ['company', 'companyname', 'organization', 'organisation', 'employer', 'account', 'accountname'],
  companyWebsite: ['companywebsite', 'website', 'domain', 'companydomain', 'url', 'companyurl'],
  linkedinUrl: ['linkedin', 'linkedinurl', 'linkedinprofile', 'personlinkedinurl', 'profileurl', 'liurl'],
  location: ['location', 'city', 'country', 'region', 'address', 'personlocation'],
  phone: ['phone', 'phonenumber', 'mobile', 'mobilephone', 'telephone', 'directphone'],
};

const normaliseHeader = (header: string) => header.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Guess which column feeds which field. Returns a map of field to header, so
 * the UI can show what it worked out and let the user correct it.
 */
export function autoMapHeaders(headers: string[]): Partial<Record<ContactField, string>> {
  const mapping: Partial<Record<ContactField, string>> = {};
  const taken = new Set<string>();
  const entries = Object.entries(HEADER_ALIASES) as [ContactField, string[]][];

  // Two passes, and the order matters. A loose alias belonging to one field
  // can otherwise swallow a column another field names exactly: "url" on
  // companyWebsite would claim "person_linkedin_url" before linkedinUrl is
  // even considered. Settling every exact match first prevents that.
  for (const [field, aliases] of entries) {
    const exact = headers.find((h) => !taken.has(h) && aliases.includes(normaliseHeader(h)));
    if (exact) {
      mapping[field] = exact;
      taken.add(exact);
    }
  }

  for (const [field, aliases] of entries) {
    if (mapping[field]) continue;
    const fuzzy = headers.find(
      (h) => !taken.has(h) && aliases.some((a) => normaliseHeader(h).includes(a)),
    );
    if (fuzzy) {
      mapping[field] = fuzzy;
      taken.add(fuzzy);
    }
  }

  return mapping;
}

/**
 * Pull contacts out of pasted text. Accepts one email or LinkedIn URL per
 * line, and tolerates lines where they are mixed with other text.
 */
export function parsePastedLines(text: string): { email?: string; linkedinUrl?: string }[] {
  const out: { email?: string; linkedinUrl?: string }[] = [];
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const linkedin = trimmed.match(/https?:\/\/[^\s,;]*linkedin\.com\/in\/[^\s,;]+/i)?.[0];
    const email = trimmed.match(/[^\s,;<>()]+@[^\s,;<>()]+\.[a-z]{2,}/i)?.[0];
    if (linkedin || email) out.push({ email, linkedinUrl: linkedin });
  }
  return out;
}
